import React from 'react';
import { useFirebase } from '../../hooks/useFirebase';
import { formatInTimeZone } from 'date-fns-tz';
import { TIME_RANGES } from '../../utils/constants';
import { computeWeatherCondition } from '../../utils/weather';
import { FirebaseReading } from '../../types/firebase';

const MIN_POINTS = 8;
const FORECAST_DAYS = 5;

const toMs = (timestamp: string | number) => {
  if (typeof timestamp === 'number') return timestamp;
  if (typeof timestamp === 'string' && /^\d+$/.test(timestamp.trim())) {
    return parseInt(timestamp.trim(), 10);
  }
  const parsed = new Date(timestamp).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

const linearSlopePerHour = (points: { t: number; v: number }[]) => {
  if (points.length < 2) return 0;
  const n = points.length;
  const meanT = points.reduce((s, p) => s + p.t, 0) / n;
  const meanV = points.reduce((s, p) => s + p.v, 0) / n;
  let num = 0;
  let den = 0;
  points.forEach((p) => {
    const dt = p.t - meanT;
    num += dt * (p.v - meanV);
    den += dt * dt;
  });
  if (den === 0) return 0;
  const slopePerMs = num / den;
  return slopePerMs * 3_600_000; // convert per ms -> per hour
};

const buildForecast = (data: FirebaseReading[]) => {
  const temps = data
    .map((d) => ({
      t: toMs(d.timestamp),
      v: d.temperature,
      h: d.humidity,
      p: d.pressure,
      l: d.light,
      r: d.rain,
      w: d.windSpeed,
    }))
    .filter((d) => d.t !== null && d.v !== null) as {
    t: number;
    v: number;
    h?: number | null;
    p?: number | null;
    l?: number | null;
    r?: number | null;
    w?: number | null;
  }[];

  if (temps.length < MIN_POINTS) return [];

  const sorted = temps.sort((a, b) => a.t - b.t);
  const slopeTemp = linearSlopePerHour(sorted.map(({ t, v }) => ({ t, v: v as number })));
  const last = sorted[sorted.length - 1];

  const forecasts = [];
  for (let i = 1; i <= FORECAST_DAYS; i++) {
    const hoursAhead = i * 24;
    const projectedTemp = (last.v as number) + slopeTemp * hoursAhead;
    const projectedHumidity = last.h ?? 60;
    const condition = computeWeatherCondition(
      projectedTemp,
      projectedHumidity,
      last.p ?? null,
      last.l ?? null,
      last.r ?? null,
      last.w ?? null
    );

    const dayDate = new Date(last.t + hoursAhead * 3_600_000);
    const dayName = formatInTimeZone(dayDate, 'Asia/Manila', 'EEEE');
    const dayNum = formatInTimeZone(dayDate, 'Asia/Manila', 'd MMM');
      
    forecasts.push({
        day: `${dayName}, ${dayNum}`,
      temp: projectedTemp.toFixed(0),
        condition,
      });
    }
    
  return forecasts;
  };

  const getConditionIcon = (condition: string): string => {
    const iconMap: Record<string, string> = {
      sunny: '/Sunny.svg',
      'partly-cloudy': '/Partly Cloudy.svg',
      cloudy: '/Cloudy.svg',
      rainy: '/Rainy.svg',
    foggy: '/Foggy.svg',
    hot: '/Hot and Dry.svg',
    cold: '/Cold.svg',
    };
    return iconMap[condition] || '/Sunny.svg';
  };

export const ForecastCard: React.FC = () => {
  const { historicalData, loadHistoricalData } = useFirebase();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        await loadHistoricalData(TIME_RANGES.DAY * 2); // use 48h to get enough signal
      } catch (e) {
        if (!active) return;
        setError('Failed to load data for forecast');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [loadHistoricalData]);

  const forecast = React.useMemo(() => buildForecast(historicalData), [historicalData]);

  const renderBody = () => {
    if (loading) {
      return (
        <div className="text-sm text-gray-400">Loading forecast from recent data...</div>
      );
    }
    if (error) {
      return <div className="text-sm text-red-400">{error}</div>;
    }
    if (forecast.length === 0) {
      return (
        <div className="text-sm text-gray-400">
          Not enough recent data to generate a forecast yet.
        </div>
      );
    }

  return (
      <div className="space-y-3">
        {forecast.map((item, index) => (
          <div key={index} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
            <div className="flex items-center gap-4">
              <img src={getConditionIcon(item.condition)} alt={item.condition} className="w-10 h-10" />
              <div>
                <div className="font-medium">{item.day}</div>
                <div className="text-sm text-gray-400 capitalize">{item.condition.replace('-', ' ')}</div>
              </div>
            </div>
            <div className="text-xl font-semibold">{item.temp}°C</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className="p-6 rounded-3xl"
      style={{
        backgroundColor: 'rgba(30, 30, 35, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <div className="text-xl font-semibold mb-4">5 Days Forecast:</div>
      {renderBody()}
    </div>
  );
};

